import { useTranslation } from 'react-i18next';
import { ShieldCheck, AlertTriangle, Info } from 'lucide-react';

const SIGNAL_KEYS = {
  headcount: 'trust.signalHeadcount',
  locations: 'trust.signalLocations',
  itSystems: 'trust.signalItSystems',
  infrastructure: 'trust.signalInfrastructure',
  maturity: 'trust.signalMaturity',
  controlReuse: 'trust.signalControlReuse',
  deliveryModel: 'trust.signalDeliveryModel',
  labourRates: 'trust.signalLabourRates',
  sector: 'trust.signalSector',
  targetTiming: 'trust.signalTargetTiming',
};

const LEVELS = {
  high: { key: 'trust.high', text: 'text-[#527F19]', bar: 'bg-[#78B928]', segments: 3, Icon: ShieldCheck },
  moderate: { key: 'trust.moderate', text: 'text-[#B7791F]', bar: 'bg-[#B7791F]', segments: 2, Icon: Info },
  low: { key: 'trust.low', text: 'text-[#B42318]', bar: 'bg-[#B42318]', segments: 1, Icon: AlertTriangle },
};

/** Model-confidence indicator that drives the planning-range width. */
export function ConfidenceIndicator({ confidence, assumptions, compact = false }) {
  const { t } = useTranslation();
  if (!confidence) return null;

  const level = LEVELS[confidence.level] || LEVELS.moderate;
  const { Icon } = level;

  if (compact) {
    return (
      <span
        className={`inline-flex flex-wrap items-center gap-1.5 max-w-full text-xs font-medium ${level.text}`}
        data-testid="estimate-confidence-compact"
      >
        <Icon className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
        <span className="min-w-0 break-words">
          {t('trust.confidenceLabel')}: {t(level.key)}
        </span>
      </span>
    );
  }

  const gaps = (confidence.gaps || []).map(g => t(SIGNAL_KEYS[g] || g));
  const defaultCount = (assumptions || []).filter(a => a.provenance === 'DEFAULT_ASSUMPTION').length;

  return (
    <div
      className="border border-[#DDE1E5] rounded-lg bg-white p-4"
      data-testid="estimate-confidence"
      aria-label={`${t('trust.confidenceLabel')}: ${t(level.key)}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.12em] text-[#66707A]"
               style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
            {t('trust.confidenceLabel')}
          </div>
          <div className={`mt-1 flex items-center gap-2 text-lg font-semibold ${level.text}`}>
            <Icon className="w-4 h-4" aria-hidden="true" />
            <span data-testid="estimate-confidence-level">{t(level.key)}</span>
          </div>
        </div>
        <div className="flex items-end gap-1 pt-1 shrink-0" aria-hidden="true">
          {[1, 2, 3].map(i => (
            <span
              key={i}
              className={`w-4 sm:w-6 rounded-sm ${i <= level.segments ? level.bar : 'bg-[#DDE1E5]'}`}
              style={{ height: `${8 + i * 5}px` }}
            />
          ))}
        </div>
      </div>

      <p className="mt-3 text-sm text-[#3A4249]" data-testid="estimate-confidence-explanation">
        {t('trust.confidenceExplain', {
          specific: confidence.specificInputs,
          total: confidence.totalInputs,
        })}
        {defaultCount > 0 && (
          <span className="text-[#66707A]"> {t('trust.confidenceDefaults', { count: defaultCount })}</span>
        )}
      </p>

      {gaps.length > 0 && (
        <p className="mt-2 text-xs text-[#66707A]" data-testid="estimate-confidence-gaps">
          <span className="text-[#8A929B]">{t('trust.confidenceImprove')} </span>
          {gaps.join(' · ')}
        </p>
      )}

      <p className="mt-3 pt-3 border-t border-[#DDE1E5] text-xs text-[#8A929B]">
        {t('trust.confidenceHint')}
      </p>
    </div>
  );
}
